import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Card, Button, Form, CardBody, Alert, Image, Container, Spinner } from 'reactstrap';
import { careInputFormGroup0, useGroupedSchema, templateForm, buildSurveyPayload, NoValueLabels, InstanceStatus, searchForOverallRating } from './_constant';
import { CenterHashCompanyList } from './../../constant';
import parseHtml from 'html-react-parser';
import { AlertContext } from '@/contexts/AlertContext'; 
import { CircularProgress } from '@mui/material';
import { MdCheckCircle  } from 'react-icons/md';
import CryptoJS from 'crypto-js';
import moment from 'moment';
import * as _API from './_API';

export const GeneralSurveyPages = ({surveyQuestionExternal, company, centre, surveyForm }) => {
  const params = (new URL(document.location)).searchParams;
  let instantId = decodeURIComponent(CryptoJS.AES.decrypt(params.get("iid") ?? "", "CareConcierge").toString(CryptoJS.enc.Utf8));
  let id = decodeURIComponent(CryptoJS.AES.decrypt(params.get("fid") ?? "", "CareConcierge").toString(CryptoJS.enc.Utf8));

  const profile = JSON.parse(window.localStorage.getItem('profile'));
  centre = centre ?? profile?.centre ?? 6;

  const decodeHint =  (hint) => typeof hint === "string" && hint.includes("{") ? JSON.parse(hint ?? "{}") : typeof hint === "object" ? hint : {};
  const isRequired = (val) => {if(val === null || val === undefined) return undefined; else if (typeof val === "string") return val.toLowerCase() === "true"; return Boolean(val);};
  const checkQuestionRequired = (q) => isRequired(decodeHint(q?.question_hint)?.required) ?? (!NoValueLabels.includes(q.question_type));
  const checkQuestion = (q) => isRequired(!NoValueLabels.includes(q.question_type));
  const checkHaveValueORNot = (n, a) => (n === null && a === null)

  const { displayAlert } = useContext(AlertContext);
  const displayError = (message) => displayAlert(message, "danger");
  const displaySuccess = (message) => displayAlert(message, "success");


  const loadCondition = () =>  (instantId.length < 1 || id.length < 1);

  const [isLoading, setIsLoading] = useState(true);
  company = company ?? CenterHashCompanyList[centre]; 
  const [formValues, setFormValues] = useState([]);
  const [surveyFieldSchema, setSurveyFieldSchema] = useState(surveyQuestionExternal);
  const [surveyContent, setSurveyContent] = useState(surveyForm ?? templateForm);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const allFieldRequired = surveyFieldSchema?.filter(f => checkQuestionRequired(f)) ?? [];
  const totalFields = allFieldRequired?.length ?? 0;
  const scoredFields = allFieldRequired?.filter(f => !checkHaveValueORNot(f?.normalize_score, f?.answer_value)).length ?? 0;
  const isComplete = scoredFields === totalFields && !!totalFields;
  const simulateIsCompleted = (addLen) => (scoredFields + addLen) === totalFields && !!totalFields;

  const allField = surveyFieldSchema?.filter(f => checkQuestion(f)) ?? [];
  const totalAllFields = allField?.length ?? 0;
  const allScoredFields = allField?.filter(f => !checkHaveValueORNot(f?.normalize_score, f?.answer_value)).length ?? 0;
  const allIsComplete = allScoredFields === totalAllFields && !!totalAllFields;

  const loadSurveyForm = async () => {
    if(loadCondition()) {
      setIsLoading(false);
      displayError('No survey form ID provided.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await _API.loadSurveyForm(id, instantId);
      if (response?.status !== 200) {
        setIsLoading(false);
        displayError(`Failed to load survey form: ${JSON.stringify(response)}`);
        return;
      }
      console.log("Survey Form Response:", response?.data);
      const data = response?.data?.[0];
      console.log("Survey Form Response:", data);
      setSurveyFieldSchema(data?.questions);
      setSurveyContent(data);
      centre = data?.centre ?? centre;
      company = CenterHashCompanyList[data?.centre ?? centre] ?? company;
      if (!company) {
        displayError(`Company not found for centre ${data?.centre}`);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to load survey form:", error);
      displayError(`Failed to load survey form: ${error?.message}`);
      setIsLoading(false);
    }
  };  

  // create grouped structure
  const grouped = useGroupedSchema(surveyFieldSchema, undefined);

  useEffect(() => {
    setFormValues(prev => {
      const newValues = {};
      grouped.forEach(group => {
        group.items.forEach(item => {
          newValues[item.id] = item.defaultValue || "";
        });
      });
      return newValues;
    });
  }, [surveyFieldSchema]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value ?? "" }));
  };

  useEffect(() => {
    console.log("Form values updated:", formValues);
  }, [formValues]);

  useEffect(() => {
    console.log("Survey Form Data:", surveyContent);
    console.log("Company:", company);
    console.log("Centre:", centre);
    console.log("Form:", surveyQuestionExternal);
  }, [surveyContent]);
  
  /**
   * Render the survey header
   * @returns Header is different with CMP
   */
  const surveyHeader = () => {
    return (
      <>
      {
        isLoading ? <Spinner color="primary" /> : <div className="">
          <div fluid className="row justify-content-between">
            <div className="mt-2 col-md-4 col-sm-6 col-xs-12">
              <img
                src={company.companyLogo}
                alt={company.companyName}
                style={{ width: 'auto', height: '70px' }}
              />
            </div>
            <div className="scale-down-text-form-sub col-md-8 col-sm-6 col-xs-12">
              <figure className="mb-0">
                <blockquote class="blockquote mb-0">
                  <h5 className="mb-0"><b>{company.companyName} ({company.companyCode})</b></h5>
                </blockquote>
                <figcaption class="blockquote-footer no-before" style={{marginTop: '0'}}>
                  <p className="mb-0">{company.telephone}</p>
                  <p className="mb-0">{company.email}</p>
                  <p className="mb-0">{company.companyAddress1}</p>
                  <p className="mb-0">{company.companyAddress2}</p>
                </figcaption>
              </figure>
            </div>
          </div>
          <hr />
          <br />
          <div fluid className="align-items-end justify-content-center">
              <h3 className=""><b>{parseHtml(surveyContent?.title) ?? "--"}</b></h3>
              <p className="displayInnerHTML">{parseHtml(surveyContent?.heading ?? "--")}</p>
          </div>
        </div>
      }
      </>
    );
  }
  
  const surveyFooter = () => {
    return (
      <center>
        <div className="py-3">
          <MdCheckCircle size={120} className="text-success me-2" />
          <i><p style={{color: "#777"}}>All questions have been answered.</p></i>
          <p className="displayInnerHTML">{parseHtml(surveyContent?.tailer ?? "--")}</p>
        </div>
      </center>
    );
  }

  const handlePageChange = (page) => {
    if (page < 1 || page > pages) return;
    setCurrentPage(page);
  };

  /**
   * Handle form submission 
   * ### Should be not same
   * @param {*} values 
   */
  const __onSubmit = async (values) => {
    setIsLoading(true);
    console.log(surveyFieldSchema)
    console.log("Submitting survey form with values:", values);
    const payload = buildSurveyPayload(values, surveyFieldSchema);
    const overallRating = searchForOverallRating(values, surveyFieldSchema);
    console.log("Built survey payload:", payload);
    if(payload.length <= 0) {
      displayError("No valid responses found.");
      setIsLoading(false);
      return;
    }

    console.log("Overall Rating:", overallRating);
    if(!overallRating) {
      displayError("Overall rating is missing or unanswered.");
      setIsLoading(false);
      return;
    }

    const result = (await _API.submitSurveyAnswers(instantId, payload)).data.response;

    if (result?.affectedRows > 0) {
      setFormValues({});
      // local updates the data form payload to the surveyFieldSchema
      const byId = new Map(
        payload.map(p => [Number(p.survey_question_id), p])
      );

      setSurveyFieldSchema(prev =>
        [...prev.map(q => {
          const hit = byId.get(Number(q.question_id));
          if (!hit) return q; // untouched (category-break, unanswered, etc.)
          return {
            ...q,
            answer_value: String(hit.answer_value ?? ""),
            normalize_score: (hit.normalize_score),
          };
        })]
      );
      if(result?.affectedRows === payload.length)
        displaySuccess("✅ All questions have been submitted successfully.");
      else
        displaySuccess(`${result?.affectedRows} questions have been submitted.`);
    } else {
      displayError("❌ Failed to submit survey questions.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
      console.log("Loading survey form with instantId:", instantId, "id:", id);
    if (instantId.length > 0 && id.length > 0) {
      loadSurveyForm();
    }else {
      displayError("Invalid instantId or id.");
    }
  }, [instantId, id]);
  
  useEffect(() => {
      console.log(grouped);
  }, [grouped]);

  useEffect(() => {
    if(surveyQuestionExternal && surveyQuestionExternal.length > 0) {
      setSurveyFieldSchema(surveyQuestionExternal);
      setIsLoading(false);
    }
  }, [surveyQuestionExternal?.length]);

  const renderInput = () => (
    <>
      <div className="container">
        {/* PARTIAL vs COMPLETE alerts */}
        {scoredFields > 0 && scoredFields < totalFields && (
          <Alert color="warning" className="mt-3" style={{position: 'fixed', bottom: 20, zIndex: 9999, right: 20}}>
            Part of the questions is already been submitted.
          </Alert>
        )}
        {(isComplete) && (
          <Alert color="success" className="mt-3" style={{position: 'fixed', bottom: 20, zIndex: 9999, right: 20}}>
            This form has been completed.
          </Alert>
        )}
        {isComplete && (surveyFooter())}
        {surveyHeader()}
        {surveyContent?.instance?.caregiver_id && careInputFormGroup0({
          display: "This survey is rating for caregiver: <b>"+ surveyContent?.instance?.caregiver_name +"</b>",
          name: "caregiver",
          type: "category-break",
          disabled: isComplete,
          defaultValue: undefined,
          value: formValues["caregiver"] ?? false,
          onEditFunc: (e) => {handleChange(e)},
          hints: "{}"
        })}
        {grouped.map((group) => (
          <Card key={group.id} className="p-3 mb-4">
            {/* Category heading */}
            <h5 className="p-2 mb-1">
              <div className="displayInnerHTML">
                {parseHtml(group?.title ?? "")}
              </div>
            </h5>
            {((group?.items?.length ?? 0) > 0) && <CardBody>
            <Form>
              {group.items.map((field, idx) => (
                <React.Fragment key={field.id}>
                  {careInputFormGroup0({
                    display: field.question_text,
                    name: field.question_id,
                    type: field.question_type,
                    disabled: !checkHaveValueORNot(field.normalize_score, field.answer_value),
                    defaultValue: field.defaultValue,
                    value: formValues[field.question_id] ?? field.answer_value,
                    onEditFunc: handleChange,
                    hints: field.question_hint || "{}",
                    required: checkQuestionRequired(field)
                  })}

                  {/* separator between questions */}
                  {idx < group.items.length - 1 && <hr />}
                </React.Fragment>
              ))}

              {/* Only show Submit on the last category */}
              {group === grouped[grouped.length - 1] && (
                <div className="text-center mt-3">
                  {
                    currentPage > 1 && (
                      <Button color="secondary" className="ms-2" outline onClick={() => handlePageChange(currentPage - 1)} disabled={isLoading || isComplete}>
                        Previous
                      </Button>
                    )
                  }
                  <Button disabled={isLoading || allIsComplete} style={{ backgroundColor:'#5a46aa' }} onClick={async () => {
                    if (Object.keys(formValues).length === 0) {
                      displayError("Please fill out the survey before submitting.");
                      return;
                    }
                    handlePageChange(currentPage + 1);
                    if (currentPage === pages) try{
                      await __onSubmit(formValues);
                    } catch (error) {
                      console.error("Error submitting survey:", error);
                      displayError("An error occurred while submitting the form." + error.message);
                    };
                  }}>
                    {currentPage < pages ? "Next" : "Submit"}
                  </Button>
                </div>
              )}
            </Form>
            </CardBody>}
          </Card>
        ))}
      </div>
    </>
  );

  return  <>
    {
    isLoading
    ? <center className='m-3' ><CircularProgress size="6rem" style={{ color:'#5a46aa' }}/></center>
    : renderInput()
    }

  </>
};

export default GeneralSurveyPages;
